import {i18n} from '@online-offer/i18n';
import {Alert} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {
  FinancialProductOffer,
  FinancialProductPreferenceRefusal,
} from '../../api/types/onlineOfferTypes';
import {getFinancialProductUnavailableReasonLabelKey} from './getFinancialProductUnavailableReasonLabelKey';

/**
 * Retried failures stay silent; a signed snapshot supersedes the contract-closed refusal.
 */
const toRefusalNotice = (refusal: FinancialProductPreferenceRefusal | null) =>
  match(refusal)
    .with({kind: 'REFUSED', reason: 'PREFERENCE_REVISION_CONFLICT'}, () => ({
      variant: 'info' as const,
      message: i18n.t('financialProducts.preference.sellerChanged'),
    }))
    .with({kind: 'REFUSED', reason: 'NOT_IN_OFFER_STATE'}, () => ({
      variant: 'warning' as const,
      message: i18n.t('financialProducts.preference.noLongerChangeable'),
    }))
    .with({kind: 'REFUSED', reason: 'PRODUCT_UNAVAILABLE'}, () => ({
      variant: 'warning' as const,
      message: i18n.t('financialProducts.preference.choiceUnavailable'),
    }))
    .otherwise(() => null);

interface FinancialProductNoticesProps extends TestIdProps {
  offer: FinancialProductOffer;
  refusal: FinancialProductPreferenceRefusal | null;
  rebased: boolean;
}

export function FinancialProductNotices(props: FinancialProductNoticesProps) {
  const notice = toRefusalNotice(props.refusal);
  const showsRefusal = isNotNil(notice);
  const showsRebased = props.offer.availability === 'UNAVAILABLE' && props.rebased;
  const showsUnavailable = props.offer.availability === 'UNAVAILABLE' && !props.rebased;

  if (!showsRefusal && !showsRebased && !showsUnavailable) {
    return null;
  }

  const unavailableLabelKey = getFinancialProductUnavailableReasonLabelKey(
    props.offer.unavailableReason
  );

  return (
    <VStack spacing={2}>
      <Show when={showsRefusal}>
        <Alert
          type="inline"
          variant={notice?.variant}
          message={notice?.message}
          data-testid={suffixTestId('preferenceNotice', props)}
        />
      </Show>
      <Show when={showsRebased}>
        <Alert
          type="inline"
          variant="info"
          message={i18n.t('financialProducts.notice.rebased')}
          data-testid={suffixTestId('rebased', props)}
        />
      </Show>
      <Show when={showsUnavailable}>
        <Alert
          type="inline"
          variant="warning"
          title={i18n.t('financialProducts.unavailable.title')}
          message={i18n.t(unavailableLabelKey)}
          data-testid={suffixTestId('unavailable', props)}
        />
      </Show>
    </VStack>
  );
}
